import { Moon, Sun, Monitor, RotateCcw } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "@/hooks/use-toast";
import { useSettings } from "@/providers/SettingsProvider";
import { useTranslation } from "react-i18next";

export const ColorModeSettings = () => {
  const { t } = useTranslation();
  const { 
    colorMode, 
    setColorModeValue, 
    customColor, 
    setCustomColorValue, 
    resetToDefaultColor,
    ravePartyMode,
    toggleRavePartyMode
  } = useSettings();

  // Handle color mode changes
  const handleColorModeChange = (value: "light" | "dark" | "auto" | "custom") => {
    setColorModeValue(value);
    
    toast({
      title: `${value.charAt(0).toUpperCase() + value.slice(1)} Mode Activated`,
      description: "Your display preferences have been updated",
    });
  };
  
  // Handle custom color change
  const handleCustomColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newColor = e.target.value;
    setCustomColorValue(newColor);
  };
  
  // Reset to default color
  const handleResetToDefaultColor = () => {
    resetToDefaultColor();
    
    toast({
      title: "Default Colors Restored",
      description: "Color settings have been reset to default",
    });
  };

  return (
    <div className="space-y-2">
      <label className="text-xs font-medium text-white/70 flex items-center">
        {colorMode === "dark" ? (
          <Moon className="h-3.5 w-3.5 mr-1.5" />
        ) : colorMode === "auto" ? (
          <Monitor className="h-3.5 w-3.5 mr-1.5" />
        ) : (
          <Sun className="h-3.5 w-3.5 mr-1.5" />
        )}
        Color Mode
      </label>
      <Select 
        value={colorMode} 
        onValueChange={(value) => handleColorModeChange(value as "light" | "dark" | "auto" | "custom")}
        onOpenChange={(open) => {
          // Prevent propagation when opening/closing the select
          if (open) {
            setTimeout(() => {
              const content = document.querySelector('.select-content');
              if (content) {
                content.classList.add('select-content');
              }
            }, 0);
          }
        }}
      >
        <SelectTrigger className="h-7 text-xs">
          <SelectValue placeholder="Select color mode" />
        </SelectTrigger>
        <SelectContent className="select-content bg-background z-50">
          <SelectItem value="light">{t("settings.displaySettings.colorMode.light")}</SelectItem>
          <SelectItem value="dark">{t("settings.displaySettings.colorMode.dark")}</SelectItem>
          <SelectItem value="auto">{t("settings.displaySettings.colorMode.auto")}</SelectItem>
          <SelectItem value="custom">Custom</SelectItem>
        </SelectContent>
      </Select>
      
      {colorMode === "custom" && (
        <div className="flex flex-col gap-2 pt-1">
          <div className="flex items-center justify-between">
            <label className="text-xs text-white/70">Theme Color:</label>
            <input
              type="color"
              value={customColor}
              onChange={handleCustomColorChange}
              className="h-6 w-6 rounded border-0 bg-transparent cursor-pointer"
            />
          </div>
          <button
            onClick={handleResetToDefaultColor}
            className="flex items-center text-xs text-white/70 hover:text-white"
          >
            <RotateCcw className="h-3 w-3 mr-1" />
            Reset to default
          </button>
        </div>
      )}
      
      {/* Rave Party Mode Checkbox */}
      <div className="flex items-center space-x-2 pt-2 border-t border-white/10">
        <Checkbox
          id="rave-party-mode"
          checked={ravePartyMode}
          onCheckedChange={toggleRavePartyMode}
          className="h-4 w-4"
        />
        <label
          htmlFor="rave-party-mode"
          className="text-xs text-white/70 cursor-pointer select-none"
        >
          {t("settings.displaySettings.colorMode.ravePartyMode")}
        </label>
      </div>
    </div>
  );
};
